import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Pagination from '@amzn/meridian/pagination';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import TextLink from 'src/components/shared/textLink/textLink';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { restrictionsHaveError } from 'src/hooks/restrictionValidation/restrictionsValidator';
import { useFetchTaskGroups } from 'src/hooks/restrictionValidation/useFetchTaskGroups';
import { type PlacementDetailsWorkRestriction } from 'src/store/placementDetailsPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { getBodyLocationLabel } from 'src/utils/getBodyLocationLabel';
import { removeSpecialCharactersFromString } from 'src/utils/helpers';
import { JmrFlowTypes } from 'src/utils/jmrUtils';
import { sortAndPaginateTableData } from 'src/utils/tableUtils';

import ConfirmUpdateRestrictionsModal from '../../confirmUpdateRestrictionsModal/confirmUpdateRestrictionsModal';

interface OnSortParams {
  sortColumn: keyof PlacementDetailsWorkRestriction;
  sortDirection: TableSortDirection;
}

/**
 * Default component.
 */
const Restrictions = () => {
  const navigate = useNavigate();
  const { featureFlags, isFeatureFlagsLoaded } = useAppSelector((state) => state.user);
  const { placementId, placementDetails } = useAppSelector((state) => state.placementDetailsPage);
  const [sortColumn, setSortColumn] = useState<keyof PlacementDetailsWorkRestriction>('taskName');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const [sortedTableData, setSortedTableData] = useState(placementDetails.workRestrictions);
  const [isRestrictionEntriesErrorModalOpen, setIsRestrictionEntriesErrorModalOpen] = useState(false);

  const { isLoading: isLoadingTaskGroups, taskGroups: restrictionValidationTaskGroups } = useFetchTaskGroups(
    isFeatureFlagsLoaded,
    featureFlags,
    placementDetails.regionId,
  );
  const mustUpdateRestrictions = useMemo(() => {
    return restrictionsHaveError(
      placementDetails.workRestrictions,
      placementDetails.modifiedShiftLength,
      restrictionValidationTaskGroups,
    );
  }, [placementDetails.workRestrictions, placementDetails.modifiedShiftLength, restrictionValidationTaskGroups]);

  // Setup sort function
  const onSort = (params: OnSortParams) => {
    setSortDirection(params.sortDirection);
    setSortColumn(params.sortColumn);
  };

  const updateRestrictions = () => {
    navigate(`/newJMR/${placementId}/update/${JmrFlowTypes.NewRestrictions}`);
  };

  // Filter, sorting, and pagination
  useEffect(() => {
    if (placementDetails?.workRestrictions) {
      const { paginatedData, totalPages } = sortAndPaginateTableData(
        [...placementDetails.workRestrictions],
        sortColumn,
        sortDirection,
        currentPage,
        5,
      );
      setSortedTableData(paginatedData);
      setTotalPages(totalPages);
    }
  }, [sortColumn, sortDirection, placementDetails.workRestrictions, currentPage]);

  return (
    <>
      <Column alignmentHorizontal="center" spacing="400">
        {/* Display "Restriction Has Errors" alert */}
        {!isLoadingTaskGroups && mustUpdateRestrictions && (
          <Alert type="error" width="100%">
            Please update this case's work restrictions using our new entry process.
            <TextLink onClick={() => setIsRestrictionEntriesErrorModalOpen(true)}>Update restrictions</TextLink>
          </Alert>
        )}

        {/* Table */}
        <Table
          headerRows={1}
          rowComponents={[UncontrolledExpandableRow, TableRow]}
          showStripes={true}
          onSort={onSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          showDividers={true}
          data-cy="placement-details__restrictions__table"
        >
          <TableRow>
            <TableCell sortColumn="taskName">Restrictions</TableCell>
            <TableCell sortColumn="bodyLocation">Body Location</TableCell>
            <TableCell data-cy="placement-details__hours-column" sortColumn="restrictedValue">
              Hours
            </TableCell>
            <TableCell sortColumn="restrictedValue">Yes/No</TableCell>
          </TableRow>
          {sortedTableData?.map((restriction) => (
            <TableRow key={restriction.taskId} data-cy="placement-details__restrictions_task_row">
              <TableCell
                data-cy={`placement-details__restrictions__task-${removeSpecialCharactersFromString(restriction.taskName)}`}
              >
                {restriction.taskName}
              </TableCell>
              <TableCell>{restriction.bodyLocation && getBodyLocationLabel(restriction.bodyLocation)}</TableCell>
              <TableCell>{restriction.hoursTypeTask && restriction.restrictedValue}</TableCell>
              <TableCell>{restriction.yesNoTypeTask && restriction.restrictedValue}</TableCell>
            </TableRow>
          ))}
        </Table>

        {/* Pagination */}
        <Pagination
          numberOfPages={totalPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
          showSkipArrows={true}
          data-cy="restrictions-table-pagination"
        />
      </Column>
      <ConfirmUpdateRestrictionsModal
        open={isRestrictionEntriesErrorModalOpen}
        onClose={() => setIsRestrictionEntriesErrorModalOpen(false)}
        submit={updateRestrictions}
      />
    </>
  );
};

export default Restrictions;
